import React, { useCallback, useMemo, useState } from 'react'
import { Attachment } from '@/checkout-instore-legacy/product/typings'

import BaseModal from './BaseModal'
import { AttachmentForm, SubmitProps } from './AttachmentForm'
import { Stepper } from './Stepper'
import { useStepperContext } from './context/StepperContext'
import { useIntl } from '@/checkout-instore-legacy/hooks/intl'
import { AttachmentSummary } from './AttachmentSummary'
import { AttachmentModalBaseProps } from './AttachmentModal'

interface MultipleAttachmentsProps extends AttachmentModalBaseProps {
  attachments: Attachment[]
}

const mapToArray = (mapValues: Map<number, SubmitProps>) =>
  Array.from(mapValues).map(([, value]) => value)

export const MultipleAttachments = ({
  isOpen,
  attachments,
  onSubmit,
  onClose,
  ...rest
}: MultipleAttachmentsProps) => {
  const { formatMessage } = useIntl()

  const [formsValues, setFormsValues] = useState<Map<number, SubmitProps>>(
    new Map<number, SubmitProps>()
  )
  const [editingValues, setEditingValues] = useState<SubmitProps>()

  const {
    currentStep,
    isLastStep,
    nextStep,
    goToStep,
    goToLastStep,
  } = useStepperContext()

  const formSubmitLabel = useMemo<string>(() => {
    if (editingValues) {
      return 'attachments.modal.save-personalization'
    }

    return 'attachments.modal.next-step'
  }, [editingValues])

  const modalTitle = useMemo<string>(() => {
    if (isLastStep) {
      return formatMessage({ id: 'attachments.modal.summary.title' })
    }

    return formatMessage(
      { id: 'attachments.modal.title-steps' },
      { step: currentStep, total: attachments.length }
    )
  }, [attachments.length, currentStep, formatMessage, isLastStep])

  const handleSubmit = useCallback(
    (values: SubmitProps) => {
      setFormsValues((previewState) => {
        const state = new Map(previewState)
        state.set(currentStep, values)
        return state
      })

      if (editingValues) {
        goToLastStep()
        setEditingValues(undefined)
      } else {
        nextStep()
      }
    },
    [currentStep, editingValues, goToLastStep, nextStep]
  )

  const handleSummary = useCallback(() => {
    onSubmit(mapToArray(formsValues))
  }, [formsValues, onSubmit])

  const steps = useMemo(() => {
    const attachmentForms = attachments.map((attachment, index) => {
      return (
        <AttachmentForm
          key={`${attachment.id}-${index}`}
          attachment={attachment}
          initialValues={editingValues}
          formSubmitLabel={formSubmitLabel}
          onSubmit={handleSubmit}
        />
      )
    })

    return [
      ...attachmentForms,
      <AttachmentSummary
        key={`summary-${attachments.length}`}
        attachments={attachments}
        formsValues={formsValues}
        onEdit={(step) => {
          goToStep(step)
          setEditingValues(formsValues?.get(step))
        }}
        onSubmit={handleSummary}
      />,
    ]
  }, [
    attachments,
    editingValues,
    formSubmitLabel,
    formsValues,
    goToStep,
    handleSummary,
    handleSubmit,
  ])

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={modalTitle} {...rest}>
      <Stepper steps={steps} />
    </BaseModal>
  )
}
